import { GlobalConfigType } from 'actions/global.config.type'
import cloneDeep from 'clone-deep'
import { FieldOrigin, InputType, IVisibleProp, LoanFieldProps, loanOptionsConstants } from 'config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { store } from 'reducers'
import { getNewSelectionOptions } from 'utils/convertor'
import { applyOptionVisibleLogic, filterOptionsByVisibleLogic } from 'utils/loan'

/** Section toggles + rental fields rendered in dedicated blocks (not the main field grid). */
export const propertyExtraSectionKeys = ['otherMortgageLoans', 'rentalIncome', 'giftsOrGrants'] as const

export const rentalIncomeFieldKeys = [
  'rentalIncomeDoesNotApply',
  'rentalGrossIncome',
  'rentalVacancyFactor',
  'rentalPiti',
  'rentalCashFlow',
] as const

export const ignoredFields = ['calculatedDscr', ...propertyExtraSectionKeys, ...rentalIncomeFieldKeys]

/**
 * Former URLA-only Property Information fields. Show only when Proposed Occupancy
 * is Owner Occupied (applies to all portals via visiblePropertyFieldsLogic).
 * proposedOccupancy itself stays visible so the choice can be made.
 */
export const ownerOccupiedOnlyFields = [
  'lienPayoff',
  'marketRentFromAppraisal',
  'proposedLoanAmount',
  'loanWith',
  'transactionType',
  'propertyTBD',
  'propertyHasNoAddress',
  'hasUnitNo',
  'isMixedUseProperty',
  'manufacturedHousing',
] as const

/** Full titles shown on the loan application when the section is visible. */
export const propertyExtraSectionTitles: Record<(typeof propertyExtraSectionKeys)[number], string> = {
  otherMortgageLoans: 'Other New Mortgage Loans on the Property You are Buying or Refinancing',
  rentalIncome: 'Rental Income on the Property You Want to Purchase',
  giftsOrGrants: 'Gifts or Grants You Have Been Given or Will Receive for this Loan',
}

/** Short labels for Loan Field Properties (avoid duplicating the long form title). */
export const propertyExtraSectionAdminTitles: Record<(typeof propertyExtraSectionKeys)[number], string> = {
  otherMortgageLoans: 'Other Mortgage Loans',
  rentalIncome: 'Rental Income',
  giftsOrGrants: 'Gifts or Grants',
}

export const defaultInputs = (_fieldProps: Record<string, IVisibleProp> | null = null): Record<string, InputType> => {
  const inputs: Record<string, InputType> = {
    otherMortgageLoans: {
      inputType: 'ToggleButton',
      title: propertyExtraSectionAdminTitles.otherMortgageLoans,
      span: 'full',
    },
    rentalIncome: {
      inputType: 'ToggleButton',
      title: propertyExtraSectionAdminTitles.rentalIncome,
      span: 'full',
    },
    giftsOrGrants: {
      inputType: 'ToggleButton',
      title: propertyExtraSectionAdminTitles.giftsOrGrants,
      span: 'full',
    },
    subjectPropertyAddress: {
      inputType: 'map',
      title: 'Property Address',
      value: '',
      error: '',
      required: true,
    },
    propertyCounty: {
      inputType: 'text',
      title: 'Property County',
      options: [],
      value: '',
      error: '',
      required: true,
    },
    apartmentUnit: {
      inputType: 'text',
      type: 'number',
      title: 'Apartment/Unit #',
      value: '',
      error: '',
    },
    propertyType: {
      inputType: 'select',
      title: 'Property Type',
      options: loanOptionsConstants.propertyType,
      hasDefaultOption: true,
      required: true,
    },
    parcelNo: {
      // "Parcel No / APN" rather than "Parcel No": servicing and outside counsel both reported the
      // APN as missing from this screen when the field was here all along, holding a county's
      // parcel number under the one name they do not use for it.
      inputType: 'text',
      title: 'Parcel No / APN',
      value: '',
      error: '',
    },
    legalDescription: {
      // Full width and a textarea: a metes-and-bounds description runs to several hundred
      // characters and a single-line input makes it unreadable and unverifiable.
      inputType: 'textarea',
      title: 'Legal Description',
      span: 'full',
      value: '',
      error: '',
    },
    nyBlock: {
      inputType: 'text',
      title: 'Block Number',
      value: '',
      visible: false,
      error: '',
    },
    nyLot: {
      inputType: 'text',
      title: 'Lot Number',
      value: '',
      visible: false,
      error: '',
    },
    numberOfUnits: {
      inputType: 'text',
      type: 'number',
      title: 'Number of Units',
      value: '',
      error: '',
      required: true,
    },
    proposedMonthlyRent: {
      title: 'Proposed Monthly Rent',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyTaxes: {
      title: 'Proposed Monthly Taxes',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyInsurance: {
      title: 'Proposed Monthly Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyHoaDues: {
      title: 'Proposed Monthly HOA Dues',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    secondLienPayment: {
      title: 'Additional Lien Payment',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
      disabled: true,
    },
    floodInsurance: {
      title: 'Flood Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    schoolTax: {
      title: 'School Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    otherTax: {
      title: 'Other Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    calculatedDscr: {
      title: 'Calculated DSCR',
      inputType: 'text',
      type: 'text',
      prefix: '%',
      readOnly: true,
    },
    yearAcquired: {
      inputType: 'text',
      type: 'number',
      title: 'Year Acquired',
    },
    lienPayoff: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Amount Existing Liens',
      prefix: '$',
    },
    cdaValue: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'CDA Value',
      error: '',
      prefix: '$',
    },
    marketRentFromAppraisal: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Market Rent From Appraisal',
      error: '',
      prefix: '$',
    },
    proposedLoanAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Proposed Loan Amount',
      error: '',
      prefix: '$',
      required: true,
    },
    loanWith: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Loan w/MIP/FF',
      error: '',
      prefix: '$',
      required: true,
    },
    transactionType: {
      inputType: 'select',
      title: 'Transaction Type',
      options: loanOptionsConstants.transactionType,
      hasDefaultOption: true,
      required: true,
    },
    propertyTBD: {
      inputType: 'checkbox',
      title: 'Property TBD',
    },
    propertyHasNoAddress: {
      inputType: 'checkbox',
      title: 'Property Has No Address',
    },
    hasUnitNo: {
      inputType: 'checkbox',
      title: 'Has Unit #',
    },
    proposedOccupancy: {
      inputType: 'select',
      title: 'Proposed Occupancy',
      options: loanOptionsConstants.proposedOccupancy,
      hasDefaultOption: true,
      required: true,
    },
    isMixedUseProperty: {
      inputType: 'select',
      title: 'Is Mixed Use Property',
      options: loanOptionsConstants.isMixedUseProperty,
      hasDefaultOption: true,
    },
    manufacturedHousing: {
      inputType: 'checkbox',
      title: 'Manufactured Housing',
    },
    rentalIncomeDoesNotApply: {
      inputType: 'checkbox',
      title: 'Does Not Apply',
      value: false,
      error: '',
    },
    rentalGrossIncome: {
      title: 'Gross Rental Income',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      value: '',
      error: '',
    },
    rentalVacancyFactor: {
      title: 'Vacancy Factor',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      value: '',
      error: '',
    },
    rentalPiti: {
      title: 'PITI',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      value: '',
      error: '',
    },
    rentalCashFlow: {
      title: 'Cash Flow',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      value: '',
      error: '',
    },
  }

  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = _fieldProps || loanFieldProperties[FieldOrigin.PropertyInformation]

  fieldProps &&
    Object.keys(fieldProps).map((key) => {
      if (!isCustomFieldKey(key)) {
        // A built-in field keeps its own definition, but admin owns its option list. Configured
        // options carry a renamed title and a visible flag, so resolve them against the field's
        // own list the way Loan Structure does rather than assigning them straight over.
        const configOptions = fieldProps[key]?.options
        const inputOptions = (inputs[key] as any)?.options
        if (inputOptions && Array.isArray(configOptions) && configOptions.length)
          (inputs[key] as any).options = getNewSelectionOptions(
            inputOptions,
            applyOptionVisibleLogic(configOptions as any) as any,
          )
        return
      }
      inputs[key] = cloneDeep(fieldProps[key] as any)
      if (Array.isArray((inputs[key] as any).options)) {
        ;(inputs[key] as any).options = filterOptionsByVisibleLogic((inputs[key] as any).options)
      }
    })

  return inputs
}
